import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Smartphone, Layers, Gauge, Wifi } from 'lucide-react';
const features = [{
  icon: Layers,
  title: 'Up to 10 zones',
  body: 'Independent control room by room'
}, {
  icon: Gauge,
  title: 'Per room temperature',
  body: 'Set the bedroom cooler than the lounge'
}, {
  icon: Smartphone,
  title: 'iPhone and Android',
  body: 'Run it from anywhere with Wi-Fi'
}, {
  icon: Wifi,
  title: 'Voice control ready',
  body: 'Works with Alexa and Google'
}];
const zones = [{
  name: 'Master',
  temp: '21°',
  active: true
}, {
  name: 'Kids 1',
  temp: '22°',
  active: true
}, {
  name: 'Lounge',
  temp: '23°',
  active: true
}, {
  name: 'Kitchen',
  temp: 'off',
  active: false
}, {
  name: 'Office',
  temp: 'off',
  active: false
}, {
  name: 'Guest',
  temp: 'off',
  active: false
}];
export function SmartZone() {
  return <section className="bg-cream py-20 md:py-28" data-id="element-692">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-693">
        <div className="bg-brand text-white rounded-3xl overflow-hidden relative shadow-2xl" data-id="element-694">
          <div className="grid lg:grid-cols-12 gap-0 items-stretch" data-id="element-695">
            {/* Content */}
            <div className="lg:col-span-7 p-8 sm:p-12 lg:p-16" data-id="element-696">
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.6
            }} data-id="element-697">
                <div className="inline-flex items-center gap-2 px-3 h-7 rounded-full bg-white/10 text-xs font-semibold uppercase tracking-widest mb-6 text-white/90" data-id="element-698">
                  <Smartphone size={13} data-id="element-699" /> Powered by iZone
                </div>
                <h2 className="text-3xl md:text-5xl font-extrabold tracking-tight leading-[1.1] mb-6" data-id="element-700">
                  Pay only for the rooms you use.
                </h2>
                <p className="text-lg leading-relaxed text-white/85 max-w-xl mb-8" data-id="element-701">
                  We pair every ducted install with{' '}
                  <strong className="text-white" data-id="element-702">iZone Smart Home</strong>,
                  Australian made zone control that lets you run only the rooms
                  you are in, from your phone, with per room temperature, fan
                  speed and timers. Saves around 40% on running costs.
                </p>

                <div className="grid sm:grid-cols-2 gap-3 mb-9" data-id="element-703">
                  {features.map((feature, i) => {
                  const Icon = feature.icon;
                  return <div key={i} className="flex items-start gap-3 p-4 rounded-xl bg-white/[0.06] border border-white/10" data-id="element-704">
                        <div className="w-9 h-9 rounded-lg bg-accent text-ink flex items-center justify-center flex-shrink-0" data-id="element-705">
                          <Icon size={16} data-id="element-706" />
                        </div>
                        <div data-id="element-707">
                          <div className="font-semibold text-sm leading-tight mb-0.5" data-id="element-708">
                            {feature.title}
                          </div>
                          <div className="text-xs text-white/70 leading-snug" data-id="element-709">
                            {feature.body}
                          </div>
                        </div>
                      </div>;
                })}
                </div>

                <a href="/smart-zone/" className="inline-flex items-center gap-2 h-14 px-8 rounded-xl bg-accent hover:bg-accent-600 text-ink text-base font-bold transition-all hover:scale-[1.02] shadow-lg shadow-accent/20" data-id="element-710">
                  Explore iZone Smart Home
                  <ArrowRight size={16} strokeWidth={2.5} data-id="element-711" />
                </a>
              </motion.div>
            </div>

            {/* Visual side: SVG phone mockup */}
            <div className="lg:col-span-5 relative bg-brand-700 p-8 lg:p-12 flex items-center justify-center min-h-[400px] lg:min-h-[600px] overflow-hidden" data-id="element-712">
              <div className="absolute inset-0 opacity-[0.08]" style={{
              backgroundImage: 'linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)',
              backgroundSize: '32px 32px'
            }} data-id="element-713"></div>

              <motion.div initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.6,
              delay: 0.3
            }} className="relative w-[240px] sm:w-[280px] aspect-[9/19] bg-ink rounded-[36px] border-[6px] border-stone-900 shadow-2xl overflow-hidden" data-id="element-714">
                <div className="px-5 pt-3 pb-1 flex items-center justify-between text-[10px] text-white/80 font-medium" data-id="element-715">
                  <span className="tabular-nums" data-id="element-716">9:41</span>
                  <span data-id="element-717">iZone</span>
                </div>

                <div className="px-5 pt-4 pb-5" data-id="element-718">
                  <div className="text-[10px] uppercase tracking-widest text-white/50 font-semibold mb-1" data-id="element-719">
                    Your home
                  </div>
                  <div className="text-white text-2xl font-bold tracking-tight leading-tight" data-id="element-720">
                    24°C <span className="text-accent" data-id="element-721">●</span>
                  </div>
                  <div className="text-[10px] text-white/60 mt-0.5" data-id="element-722">
                    3 of 6 zones active
                  </div>
                </div>

                <div className="px-3 space-y-2" data-id="element-723">
                  {zones.map((zone, i) => <motion.div key={i} initial={{
                  opacity: 0,
                  x: -10
                }} whileInView={{
                  opacity: 1,
                  x: 0
                }} viewport={{
                  once: true
                }} transition={{
                  duration: 0.3,
                  delay: 0.6 + i * 0.05
                }} className={`flex items-center justify-between px-3 py-2.5 rounded-xl ${zone.active ? 'bg-accent/20 border border-accent/40' : 'bg-white/[0.04] border border-white/10'}`} data-id="element-724">
                      <span className={`text-xs font-medium ${zone.active ? 'text-white' : 'text-white/40'}`} data-id="element-725">
                        {zone.name}
                      </span>
                      <span className={`text-xs tabular-nums font-semibold ${zone.active ? 'text-accent' : 'text-white/30'}`} data-id="element-726">
                        {zone.temp}
                      </span>
                    </motion.div>)}
                </div>
              </motion.div>

              <motion.div initial={{
              opacity: 0,
              x: 30
            }} whileInView={{
              opacity: 1,
              x: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.6,
              delay: 0.7
            }} className="absolute top-12 right-6 lg:right-8 bg-white text-ink rounded-2xl px-4 py-3 shadow-2xl" data-id="element-727">
                <div className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold" data-id="element-728">
                  Savings vs no zoning
                </div>
                <div className="text-2xl font-extrabold tracking-tight tabular-nums text-brand" data-id="element-729">
                  ~40%
                </div>
                <div className="text-[10px] text-stone-500" data-id="element-730">
                  power use, on average
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}